import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import LockOpenOutlinedIcon from '@material-ui/icons/LockOpenOutlined';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import SecurityIcon from '@material-ui/icons/Security';
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKeyOutlined';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  clear,
  disable,
  enable,
  load,
  LOAD,
  ENABLE,
  DISABLE,
  RESET,
  reset,
  STATUS_OUTCOME_NOT_FOUND,
} from 'model/palIfpCredential';
import { handleStopPropagation } from 'consts';
import {
  selector,
  selectorLastReset,
  selectorStatusOutcome,
} from 'selectors/palIfpCredential';
import { isLoadingState } from 'reducers/loading';
import { addMessage } from 'reducers/successMessage';

const CHANNEL_EMAIL = 'email';
const CHANNEL_SMS = 'sms';
const CONFIRM_LOCK = 'lock';
const CONFIRM_UNLOCK = 'unlock';
const CONFIRM_RESET = 'reset';
const TITLE_ID = 'login-failures-credential-title';
const ACCENT = '#732282';
const CANCEL_COLOR = '#777777';

const buildIdentifierPayload = (email, phone) => {
  if (email) {
    return { email };
  }
  if (phone) {
    return { phone };
  }
  return null;
};

const maskEmail = (email) => {
  if (!email) {
    return '';
  }
  const [local = '', domain = ''] = String(email).split('@');
  if (!domain) {
    return '****';
  }
  const localMask =
    local.length <= 1
      ? '*'
      : `${local[0]}${'*'.repeat(Math.min(3, local.length - 1))}`;
  const domainMask =
    domain.length <= 1
      ? '*'
      : `${domain[0]}${'*'.repeat(Math.min(6, domain.length - 1))}`;
  return `${localMask}@${domainMask}`;
};

const maskPhone = (phone) => {
  if (!phone) {
    return '';
  }
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 4) {
    return '****';
  }
  const lastFour = digits.slice(-4);
  const prefix = String(phone).startsWith('+') ? '+' : '';
  return `${prefix}${'*'.repeat(Math.max(digits.length - 4, 3))}${lastFour}`;
};

const maskContact = (contact, channel) => {
  if (!contact) {
    return '';
  }
  if (channel === CHANNEL_SMS || String(contact).startsWith('+')) {
    return maskPhone(contact);
  }
  if (String(contact).includes('@')) {
    return maskEmail(contact);
  }
  return maskPhone(contact) || '****';
};

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 8,
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.24)',
    maxWidth: 700,
    width: '100%',
  },
  confirmPaper: {
    borderRadius: 8,
    boxShadow: '0px 10px 32px rgba(0, 0, 0, 0.28)',
    maxWidth: 480,
    width: '100%',
  },
  titleRow: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
  },
  titleWithIcon: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  shieldIcon: {
    alignItems: 'center',
    backgroundColor: '#FFEAEA',
    borderRadius: '50%',
    color: ACCENT,
    display: 'flex',
    flexShrink: 0,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  title: {
    color: '#555555',
    fontSize: 18,
    fontWeight: 600,
  },
  confirmTitle: {
    color: '#555555',
    fontSize: 17,
    fontWeight: 600,
  },
  subtitle: {
    color: CANCEL_COLOR,
    fontSize: 13,
    marginTop: theme.spacing(0.5),
  },
  confirmBody: {
    color: CANCEL_COLOR,
    fontSize: 13.5,
    lineHeight: 1.45,
  },
  closeButton: {
    marginLeft: theme.spacing(1),
  },
  actionsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-start',
    marginTop: theme.spacing(1),
  },
  actionCard: {
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0E0E0',
    borderRadius: 6,
    cursor: 'pointer',
    display: 'flex',
    flex: '1 1 240px',
    gap: theme.spacing(1),
    maxWidth: theme.spacing(40),
    minHeight: 64,
    padding: theme.spacing(1, 1.5),
    textAlign: 'left',
    width: '100%',
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.55,
    },
    '&:focus': {
      outline: `2px solid ${ACCENT}`,
      outlineOffset: 2,
    },
  },
  actionIcon: {
    alignItems: 'center',
    backgroundColor: '#F3E5F5',
    borderRadius: '50%',
    color: ACCENT,
    display: 'flex',
    flexShrink: 0,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  actionTitle: {
    color: '#555555',
    fontSize: 14,
    fontWeight: 600,
  },
  actionHint: {
    color: CANCEL_COLOR,
    fontSize: 12,
  },
  loadingWrap: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 0),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: 13,
    marginTop: theme.spacing(1),
  },
  noContact: {
    color: CANCEL_COLOR,
    fontSize: 13,
    marginTop: theme.spacing(1),
  },
  notFoundPanel: {
    marginTop: theme.spacing(1),
  },
  notFoundTitle: {
    color: '#555555',
    fontSize: 14,
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  notFoundBody: {
    color: CANCEL_COLOR,
    fontSize: 13,
    lineHeight: 1.45,
  },
  cancelButton: {
    color: CANCEL_COLOR,
    textTransform: 'none',
  },
  confirmButton: {
    backgroundColor: ACCENT,
    color: '#FFFFFF',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#5c1a68',
    },
    '&:disabled': {
      backgroundColor: ACCENT,
      color: '#FFFFFF',
      opacity: 0.55,
    },
  },
  channelGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  channelCard: {
    alignItems: 'center',
    border: '1px solid #E0E0E0',
    borderRadius: 6,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    '&:focus': {
      outline: `2px solid ${ACCENT}`,
      outlineOffset: 2,
    },
  },
  channelCardSelected: {
    backgroundColor: '#F3E5F5',
    border: `2px solid ${ACCENT}`,
  },
  channelCardDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  channelIcon: {
    color: ACCENT,
  },
  channelLabel: {
    color: '#555555',
    fontSize: 13,
    fontWeight: 600,
  },
  channelValue: {
    color: CANCEL_COLOR,
    fontSize: 12,
  },
  radioOuter: {
    border: '2px solid #E0E0E0',
    borderRadius: '50%',
    flexShrink: 0,
    height: 18,
    marginLeft: 'auto',
    width: 18,
  },
  radioOuterSelected: {
    backgroundColor: ACCENT,
    borderColor: ACCENT,
  },
  resetAck: {
    backgroundColor: '#F5F5F5',
    borderRadius: 6,
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
  },
  resetAckTitle: {
    color: '#555555',
    fontSize: 13,
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  resetAckBody: {
    color: CANCEL_COLOR,
    fontSize: 13,
  },
}));

const ActionCard = ({
  classes,
  disabled,
  hint,
  icon,
  label,
  onClick,
  title,
}) => (
  <button
    aria-label={label}
    className={classes.actionCard}
    disabled={disabled}
    onClick={onClick}
    title={title}
    type="button"
  >
    <span aria-hidden className={classes.actionIcon}>
      {icon}
    </span>
    <span>
      <Typography className={classes.actionTitle} component="span">
        {label}
      </Typography>
      {hint ? (
        <Typography className={classes.actionHint} component="div">
          {hint}
        </Typography>
      ) : null}
    </span>
  </button>
);

ActionCard.propTypes = {
  classes: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  hint: PropTypes.node,
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  title: PropTypes.string,
};

ActionCard.defaultProps = {
  disabled: false,
  hint: null,
  onClick: undefined,
  title: undefined,
};

const ChannelCard = ({
  classes,
  disabled,
  icon,
  label,
  maskedValue,
  onSelect,
  selected,
  value,
}) => {
  const handleSelect = () => {
    if (!disabled) {
      onSelect(value);
    }
  };

  const handleKeyDown = (event) => {
    if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onSelect(value);
    }
  };

  return (
    <div
      aria-checked={selected}
      aria-disabled={disabled}
      aria-label={label}
      className={classNames(classes.channelCard, {
        [classes.channelCardDisabled]: disabled,
        [classes.channelCardSelected]: selected,
      })}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      role="radio"
      tabIndex={disabled ? -1 : 0}
    >
      <span aria-hidden className={classes.channelIcon}>
        {icon}
      </span>
      <span>
        <Typography className={classes.channelLabel} component="div">
          {label}
        </Typography>
        <Typography className={classes.channelValue} component="div">
          {maskedValue}
        </Typography>
      </span>
      <span
        aria-hidden
        className={classNames(classes.radioOuter, {
          [classes.radioOuterSelected]: selected,
        })}
      />
    </div>
  );
};

ChannelCard.propTypes = {
  classes: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  maskedValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  value: PropTypes.string.isRequired,
};

ChannelCard.defaultProps = {
  disabled: false,
  maskedValue: '',
  selected: false,
};

const LoginFailuresCredentialModal = ({
  email,
  enabled: enabledProp,
  error: errorProp,
  fspId,
  loading: loadingProp,
  onClose,
  open,
  payerId,
  payerName,
  phone,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [confirmMode, setConfirmMode] = useState(null);
  const [deliveryChannel, setDeliveryChannel] = useState(null);
  const [actionError, setActionError] = useState(null);

  const credential = useSelector(selector);
  const lastReset = useSelector(selectorLastReset);
  const statusOutcome = useSelector(selectorStatusOutcome);
  const isStatusLoading = useSelector((state) =>
    isLoadingState(state, LOAD.ACTION)
  );
  const isEnableLoading = useSelector((state) =>
    isLoadingState(state, ENABLE.ACTION)
  );
  const isDisableLoading = useSelector((state) =>
    isLoadingState(state, DISABLE.ACTION)
  );
  const isResetLoading = useSelector((state) =>
    isLoadingState(state, RESET.ACTION)
  );

  const hasEmail = Boolean(email);
  const hasPhone = Boolean(phone);
  const hasAnyContact = hasEmail || hasPhone;
  const identifierPayload = useMemo(
    () => buildIdentifierPayload(email, phone),
    [email, phone]
  );

  const enabled =
    credential && typeof credential.enabled === 'boolean'
      ? credential.enabled
      : enabledProp;

  const loading = Boolean(loadingProp || isStatusLoading);
  const actionPending = isEnableLoading || isDisableLoading || isResetLoading;
  const displayName = payerName || 'this payer';
  const showNotFoundEmpty =
    open &&
    !loading &&
    hasAnyContact &&
    statusOutcome === STATUS_OUTCOME_NOT_FOUND;
  const showAdminActions = !showNotFoundEmpty;

  const errorMessage = useMemo(() => {
    if (actionError) {
      return actionError;
    }
    if (errorProp == null) {
      return null;
    }
    if (typeof errorProp === 'string') {
      return errorProp;
    }
    return errorProp.message || errorProp.detail || String(errorProp);
  }, [actionError, errorProp]);

  const dispatchStatus = useCallback(() => {
    if (!open || !fspId || !identifierPayload) {
      return;
    }
    dispatch(load({ fspId, ...identifierPayload }));
  }, [dispatch, fspId, identifierPayload, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setConfirmMode(null);
    setDeliveryChannel(null);
    setActionError(null);
    dispatchStatus();
    return undefined;
  }, [open, dispatchStatus]);

  useEffect(() => {
    if (confirmMode !== CONFIRM_RESET) {
      return;
    }
    if (hasEmail && !hasPhone) {
      setDeliveryChannel(CHANNEL_EMAIL);
    } else if (hasPhone && !hasEmail) {
      setDeliveryChannel(CHANNEL_SMS);
    } else {
      setDeliveryChannel(null);
    }
  }, [confirmMode, hasEmail, hasPhone]);

  const handleClose = () => {
    if (actionPending) {
      return;
    }
    dispatch(clear());
    setConfirmMode(null);
    setDeliveryChannel(null);
    setActionError(null);
    onClose();
  };

  const closeConfirm = () => {
    if (actionPending) {
      return;
    }
    // Defer close so the Cancel click cannot fall through onto an action card.
    window.setTimeout(() => {
      setConfirmMode(null);
      setDeliveryChannel(null);
    }, 0);
  };

  const openLockConfirm = () => {
    setActionError(null);
    setConfirmMode(CONFIRM_LOCK);
  };

  const openUnlockConfirm = () => {
    setActionError(null);
    setConfirmMode(CONFIRM_UNLOCK);
  };

  const openResetConfirm = () => {
    setActionError(null);
    setConfirmMode(CONFIRM_RESET);
  };

  const handleLockUnlockConfirm = () => {
    if (!identifierPayload || !fspId) {
      return;
    }

    setActionError(null);
    const isUnlock = confirmMode === CONFIRM_UNLOCK;
    const action = isUnlock ? enable : disable;
    const successMessage = isUnlock
      ? `Account unlocked for ${displayName}.`
      : `Account locked for ${displayName}.`;

    dispatch(
      action({
        fspId,
        ...identifierPayload,
        onError: (error) => {
          setActionError(
            error?.message ||
              (isUnlock
                ? 'Unable to unlock account. Please try again.'
                : 'Unable to lock account. Please try again.')
          );
        },
        onSuccess: () => {
          dispatch(addMessage(successMessage));
          setConfirmMode(null);
        },
      })
    );
  };

  const handleResetConfirm = () => {
    if (!identifierPayload || !fspId || !deliveryChannel) {
      return;
    }

    setActionError(null);
    dispatch(
      reset({
        fspId,
        ...identifierPayload,
        channel: deliveryChannel,
        onError: (error) => {
          setActionError(
            error?.message ||
              'Unable to resend forgot password. Please try again.'
          );
        },
        onSuccess: () => {
          dispatch(addMessage('Forgot password request sent.'));
          setConfirmMode(null);
        },
      })
    );
  };

  const resetAckContact = lastReset?.contact
    ? maskContact(lastReset.contact, lastReset.channel)
    : null;
  const resetAckChannel =
    lastReset?.channel === CHANNEL_SMS
      ? 'Mobile Phone (SMS)'
      : lastReset?.channel === CHANNEL_EMAIL
      ? 'Email'
      : lastReset?.channel || null;

  const confirmTitle =
    confirmMode === CONFIRM_UNLOCK
      ? 'Unlock Account?'
      : confirmMode === CONFIRM_LOCK
      ? 'Lock Account?'
      : 'Resend Forgot Password?';

  const confirmBody =
    confirmMode === CONFIRM_UNLOCK
      ? `This will unlock ${displayName}'s account so they can sign in again.`
      : confirmMode === CONFIRM_LOCK
      ? `This will lock ${displayName}'s account. They cannot sign in until the account is unlocked.`
      : 'Choose where to send the forgot password instructions.';

  const confirmCta =
    confirmMode === CONFIRM_UNLOCK
      ? 'Unlock'
      : confirmMode === CONFIRM_LOCK
      ? 'Lock'
      : 'Resend';

  return (
    <>
      <Dialog
        aria-labelledby={TITLE_ID}
        classes={{ paper: classes.paper }}
        data-payer-id={payerId}
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        onClick={handleStopPropagation}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleClose();
        }}
        open={open}
      >
        <DialogTitle disableTypography>
          <div className={classes.titleRow}>
            <div className={classes.titleWithIcon}>
              <span
                aria-hidden
                className={classes.shieldIcon}
                data-testid="login-failures-shield-icon"
              >
                <SecurityIcon fontSize="small" />
              </span>
              <div>
                <Typography
                  className={classes.title}
                  component="h2"
                  id={TITLE_ID}
                >
                  Login Failures
                </Typography>
                <Typography className={classes.subtitle}>
                  Manage account security for this enrolled payer.
                </Typography>
              </div>
            </div>
            <IconButton
              aria-label="Close"
              className={classes.closeButton}
              disabled={actionPending}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          {!hasAnyContact ? (
            <Typography className={classes.noContact}>
              No email or phone contact is available. Add a missing contact
              before managing this enrolled account.
            </Typography>
          ) : null}

          {loading ? (
            <div className={classes.loadingWrap}>
              <CircularProgress aria-label="Loading credential status" />
            </div>
          ) : null}

          {errorMessage ? (
            <Typography className={classes.errorText} role="alert">
              {errorMessage}
            </Typography>
          ) : null}

          {resetAckContact ? (
            <div
              className={classes.resetAck}
              data-testid="password-reset-ack"
              role="status"
            >
              <Typography className={classes.resetAckTitle} component="div">
                Forgot password sent
              </Typography>
              <Typography className={classes.resetAckBody} component="div">
                Sent to {resetAckContact}
                {resetAckChannel ? ` via ${resetAckChannel}` : ''}.
              </Typography>
            </div>
          ) : null}

          {showNotFoundEmpty ? (
            <div
              className={classes.notFoundPanel}
              data-testid="login-failures-status-not-found"
              role="status"
            >
              <Typography className={classes.notFoundTitle} component="p">
                No login account found for this payer.
              </Typography>
            </div>
          ) : null}

          {showAdminActions ? (
            <div className={classes.actionsRow}>
              {enabled ? (
                <ActionCard
                  classes={classes}
                  disabled={!hasAnyContact || actionPending || loading}
                  icon={<LockOutlinedIcon fontSize="small" />}
                  label="Lock"
                  onClick={openLockConfirm}
                />
              ) : (
                <ActionCard
                  classes={classes}
                  disabled={!hasAnyContact || actionPending || loading}
                  icon={<LockOpenOutlinedIcon fontSize="small" />}
                  label="Unlock"
                  onClick={openUnlockConfirm}
                />
              )}
              <ActionCard
                classes={classes}
                disabled={!hasAnyContact || actionPending || loading}
                icon={<VpnKeyOutlinedIcon fontSize="small" />}
                label="Resend Forgot Password"
                onClick={openResetConfirm}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        aria-labelledby="credential-confirm-title"
        classes={{ paper: classes.confirmPaper }}
        data-testid="credential-confirm-dialog"
        fullWidth
        maxWidth="xs"
        onClick={handleStopPropagation}
        onClose={(_event, reason) => {
          if (
            actionPending &&
            (reason === 'backdropClick' || reason === 'escapeKeyDown')
          ) {
            return;
          }
          closeConfirm();
        }}
        open={Boolean(confirmMode)}
      >
        <DialogTitle disableTypography id="credential-confirm-title">
          <Typography className={classes.confirmTitle} component="h2">
            {confirmTitle}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography className={classes.confirmBody}>{confirmBody}</Typography>

          {confirmMode === CONFIRM_RESET ? (
            <div
              aria-label="Password reset delivery channel"
              className={classes.channelGroup}
              role="radiogroup"
            >
              <ChannelCard
                classes={classes}
                disabled={!hasEmail || actionPending}
                icon={<EmailOutlinedIcon fontSize="small" />}
                label="Email"
                maskedValue={maskEmail(email)}
                onSelect={setDeliveryChannel}
                selected={deliveryChannel === CHANNEL_EMAIL}
                value={CHANNEL_EMAIL}
              />
              <ChannelCard
                classes={classes}
                disabled={!hasPhone || actionPending}
                icon={<PhoneIphoneIcon fontSize="small" />}
                label="Mobile Phone"
                maskedValue={maskPhone(phone)}
                onSelect={setDeliveryChannel}
                selected={deliveryChannel === CHANNEL_SMS}
                value={CHANNEL_SMS}
              />
            </div>
          ) : null}

          {actionError && confirmMode ? (
            <Typography className={classes.errorText} role="alert">
              {actionError}
            </Typography>
          ) : null}

          {actionPending ? (
            <div className={classes.loadingWrap}>
              <CircularProgress aria-label="Processing credential action" />
            </div>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.cancelButton}
            disabled={actionPending}
            onClick={closeConfirm}
          >
            Cancel
          </Button>
          <Button
            className={classes.confirmButton}
            disabled={
              actionPending ||
              (confirmMode === CONFIRM_RESET && !deliveryChannel)
            }
            onClick={
              confirmMode === CONFIRM_RESET
                ? handleResetConfirm
                : handleLockUnlockConfirm
            }
            variant="contained"
          >
            {confirmCta}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

LoginFailuresCredentialModal.propTypes = {
  email: PropTypes.string,
  enabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  fspId: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  payerId: PropTypes.number.isRequired,
  payerName: PropTypes.string,
  phone: PropTypes.string,
};

LoginFailuresCredentialModal.defaultProps = {
  email: null,
  enabled: true,
  error: null,
  loading: false,
  payerName: null,
  phone: null,
};

export default LoginFailuresCredentialModal;
